import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { RefreshCw, Loader2, Search, UserX, UserCheck, ChevronLeft, ChevronRight, Gift, Mail, KeyRound, Copy, MessageCircle, Send } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 50;

function formatDateAR(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('es-AR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export default function UsersManagementSection({ adminSecret, token }) {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  // "recientes" (alta más nueva) o "entrevistas" (quién usa de verdad).
  const [orden, setOrden] = useState('recientes');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showOnlyDisabled, setShowOnlyDisabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pendingUser, setPendingUser] = useState(null); // {id, email, currentlyDisabled}
  const [submitting, setSubmitting] = useState(false);
  // Link para crear contraseña nueva (o la cuenta, si pagó y no se
  // registró), para mandar por WhatsApp.
  const [linkReset, setLinkReset] = useState(null); // {email, link, tieneCuenta?, error?}
  const [generandoLink, setGenerandoLink] = useState(null); // id del usuario

  // Reenviar el acceso por mail a quien pagó, tenga cuenta o no.
  const [showReenviar, setShowReenviar] = useState(false);
  const [reenviarEmail, setReenviarEmail] = useState('');
  const [reenviando, setReenviando] = useState(null); // mail en curso

  // Crear cuenta de prueba gratis
  const [showGrantModal, setShowGrantModal] = useState(false);
  const [grantEmail, setGrantEmail] = useState('');
  const [grantName, setGrantName] = useState('');
  const [granting, setGranting] = useState(false);

  const handleGrantSubmit = async () => {
    const email = grantEmail.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Email inválido');
      return;
    }
    setGranting(true);
    try {
      const res = await fetch(`${API}/api/admin/users/grant-free-trial`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Secret': adminSecret,
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ email, name: grantName.trim() || undefined }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      if (data.email_sent) {
        toast.success(`Cuenta creada y email enviado a ${email}`);
      } else {
        toast.warning(`Cuenta creada, pero el email no se envió. Link: ${data.reset_link_preview}`);
      }
      setShowGrantModal(false);
      setGrantEmail('');
      setGrantName('');
      // refrescar tabla
      load();
    } catch (e) {
      toast.error(e.message || 'No se pudo crear la cuenta');
    } finally {
      setGranting(false);
    }
  };

  // Debounce search (350ms) y resetear a página 1 cuando cambia
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    if (!adminSecret || !token) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ page: String(page), page_size: String(PAGE_SIZE) });
      if (debouncedSearch) params.set('search', debouncedSearch);
      params.set('orden', orden);
      const res = await fetch(`${API}/api/admin/all-users?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Admin-Secret': adminSecret,
        },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setUsers(data.users || []);
      setTotal(data.total || 0);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [adminSecret, token, page, debouncedSearch, orden]);

  useEffect(() => { load(); }, [load]);

  const generarLinkContrasena = async (u) => {
    setGenerandoLink(u.id);
    try {
      const res = await fetch(`${API}/api/admin/users/${u.id}/link-contrasena`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Admin-Secret': adminSecret,
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      setLinkReset({ email: data.email, link: data.link });
    } catch (e) {
      toast.error(e.message || 'No se pudo generar el link');
    } finally {
      setGenerandoLink(null);
    }
  };

  const reenviarAcceso = async (emailCrudo) => {
    const email = (emailCrudo || '').trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Email inválido');
      return;
    }
    setReenviando(email);
    try {
      const res = await fetch(`${API}/api/admin/reenviar-acceso`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Admin-Secret': adminSecret,
        },
        body: JSON.stringify({ email }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      setShowReenviar(false);
      setReenviarEmail('');
      if (data.email_sent) {
        toast.success(
          data.tiene_cuenta
            ? `Le mandamos a ${email} un link para crear su contraseña`
            : `Le mandamos a ${email} el link para crear su cuenta`
        );
      } else {
        // El mail no salió: el link para mandarlo por WhatsApp, con el motivo.
        setLinkReset({ email: data.email, link: data.link, tieneCuenta: data.tiene_cuenta, error: data.error });
      }
    } catch (e) {
      toast.error(e.message || 'No se pudo reenviar el acceso');
    } finally {
      setReenviando(null);
    }
  };

  const copiarLink = async () => {
    try {
      await navigator.clipboard.writeText(linkReset.link);
      toast.success('Link copiado');
    } catch {
      toast.error('No se pudo copiar (revisá los permisos del navegador)');
    }
  };

  const linkDeCuentaNueva = linkReset?.tieneCuenta === false;
  const mensajeWhatsApp = !linkReset
    ? ''
    : linkDeCuentaNueva
      ? `Hola! Tu acceso a EVALPRO ya está habilitado. Creá tu cuenta acá, con este mismo mail: ${linkReset.link}`
      : `Hola! Te paso el link para crear tu contraseña nueva de EVALPRO: ${linkReset.link}\n\nVale por 24 horas y se usa una sola vez.`;

  const handleToggleRequest = (u) => {
    setPendingUser({
      id: u.id,
      email: u.email,
      currentlyDisabled: !!u.is_disabled,
    });
  };

  const confirmToggle = async () => {
    if (!pendingUser) return;
    const { id, email, currentlyDisabled } = pendingUser;
    const action = currentlyDisabled ? 'enable' : 'disable';
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/admin/users/${id}/${action}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Admin-Secret': adminSecret,
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      // Optimistic update
      setUsers((prev) => prev.map((u) => (u.id === id ? { ...u, is_disabled: !currentlyDisabled } : u)));
      toast.success(
        currentlyDisabled
          ? `Cuenta de ${email} reactivada`
          : `Cuenta de ${email} deshabilitada`
      );
    } catch (e) {
      toast.error(e.message || 'Error al actualizar el estado');
    } finally {
      setSubmitting(false);
      setPendingUser(null);
    }
  };

  // Filtro client-side restante: solo deshabilitados (la búsqueda ya vino del backend)
  const filtered = users.filter((u) => {
    if (showOnlyDisabled && !u.is_disabled) return false;
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <section className="mb-8" data-testid="users-management-section">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h2 className="text-xs uppercase tracking-wider text-white font-semibold">
          Gestión de usuarios ({total})
        </h2>
        <div className="flex items-center gap-3 flex-wrap">
          <select
            value={orden}
            onChange={(e) => { setOrden(e.target.value); setPage(1); }}
            className="h-8 rounded-md bg-white/5 border border-white/10 text-xs text-white px-2"
            data-testid="users-orden-select"
          >
            <option value="recientes" className="text-black">Más recientes</option>
            <option value="entrevistas" className="text-black">Más entrevistas</option>
          </select>
          <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
            <Switch
              checked={showOnlyDisabled}
              onCheckedChange={setShowOnlyDisabled}
              data-testid="filter-only-disabled-switch"
            />
            Mostrar solo deshabilitados
          </label>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowReenviar(true)}
            data-testid="reenviar-acceso-btn"
            className="bg-transparent border-[#5B9B2F]/50 text-[#8BC34A] hover:bg-[#5B9B2F]/10 hover:text-white"
          >
            <Send className="h-3.5 w-3.5 mr-1.5" />
            Reenviar acceso
          </Button>
          <Button
            size="sm"
            onClick={() => setShowGrantModal(true)}
            data-testid="grant-free-trial-btn"
            className="bg-[#5B9B2F] hover:bg-[#4a8126] text-white"
          >
            <Gift className="h-3.5 w-3.5 mr-1.5" />
            Crear cuenta de prueba
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            disabled={loading}
            data-testid="users-refresh-btn"
            className="bg-transparent border-white/10 text-white hover:bg-white/5 hover:text-white"
          >
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
            Refrescar
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-3">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
        <Input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por email o nombre…"
          className="bg-white/5 border-white/10 text-white pl-9"
          data-testid="users-search-input"
        />
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 mb-3 text-red-300 text-sm">
          {error}
        </div>
      )}

      <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden" data-testid="users-table">
        <div className="overflow-x-auto max-h-[520px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-slate-400 text-xs uppercase tracking-wider sticky top-0 backdrop-blur-sm">
              <tr>
                <th className="px-4 py-3 text-left">Email</th>
                <th className="px-4 py-3 text-left">Nombre</th>
                <th className="px-4 py-3 text-left">Plan</th>
                <th className="px-4 py-3 text-left">Alta</th>
                <th className="px-4 py-3 text-right">Entrevistas</th>
                <th className="px-4 py-3 text-left">Última</th>
                <th className="px-4 py-3 text-center">Estado</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {loading && filtered.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-500">
                  <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />
                  Cargando usuarios…
                </td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-500">
                  {showOnlyDisabled
                    ? 'No hay usuarios deshabilitados en esta página'
                    : search
                      ? 'Ningún usuario coincide con la búsqueda'
                      : 'No hay usuarios para mostrar'}
                </td></tr>
              )}
              {filtered.map((u) => (
                <tr
                  key={u.id}
                  className={`hover:bg-white/5 ${u.is_disabled ? 'opacity-60' : ''}`}
                  data-testid={`user-row-${u.email}`}
                >
                  <td className="px-4 py-3 text-slate-200 break-all">{u.email}</td>
                  <td className="px-4 py-3 text-slate-300">{u.name || '—'}</td>
                  <td className="px-4 py-3 text-slate-400 text-xs uppercase">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span>{u.account_type}</span>
                      {u.account_source === 'free_trial' && (
                        <span
                          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-500/15 text-amber-300 border border-amber-500/30 normal-case"
                          data-testid={`free-trial-badge-${u.email}`}
                          title="Cuenta de prueba gratis creada por admin"
                        >
                          <Gift className="h-2.5 w-2.5" />
                          Regalo
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-400 text-xs whitespace-nowrap">{formatDateAR(u.created_at)}</td>
                  <td className="px-4 py-3 text-right text-slate-300">{u.evaluations_count ?? 0}</td>
                  <td className="px-4 py-3 text-slate-400 text-xs whitespace-nowrap">{u.ultima_entrevista ? formatDateAR(u.ultima_entrevista) : '—'}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          if (window.confirm(`¿Mandarle a ${u.email} un mail con su acceso?`)) reenviarAcceso(u.email);
                        }}
                        disabled={reenviando !== null || u.is_disabled}
                        title={u.is_disabled ? 'Habilitá la cuenta primero' : 'Reenviar el acceso por mail'}
                        className="p-1.5 rounded text-slate-400 hover:text-white hover:bg-white/10 disabled:opacity-30"
                        data-testid={`reenviar-acceso-${u.email}`}
                      >
                        {reenviando === u.email
                          ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          : <Send className="h-3.5 w-3.5" />}
                      </button>
                      <button
                        type="button"
                        onClick={() => generarLinkContrasena(u)}
                        disabled={generandoLink !== null || u.is_disabled}
                        title={u.is_disabled ? 'Habilitá la cuenta primero' : 'Link para crear contraseña nueva'}
                        className="p-1.5 rounded text-slate-400 hover:text-white hover:bg-white/10 disabled:opacity-30"
                        data-testid={`link-contrasena-${u.email}`}
                      >
                        {generandoLink === u.id
                          ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          : <KeyRound className="h-3.5 w-3.5" />}
                      </button>
                      {u.is_disabled && (
                        <span
                          className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold border bg-red-500/15 text-red-400 border-red-500/30"
                          data-testid={`disabled-badge-${u.email}`}
                        >
                          Deshabilitado
                        </span>
                      )}
                      <Switch
                        checked={!u.is_disabled}
                        onCheckedChange={() => handleToggleRequest(u)}
                        data-testid={`toggle-user-${u.email}`}
                        aria-label={u.is_disabled ? 'Habilitar usuario' : 'Deshabilitar usuario'}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-3">
          <p className="text-slate-500 text-xs">Página {page} de {totalPages}</p>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1 || loading}
              data-testid="users-prev-page"
              className="bg-transparent border-white/10 text-white hover:bg-white/5 hover:text-white"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages || loading}
              data-testid="users-next-page"
              className="bg-transparent border-white/10 text-white hover:bg-white/5 hover:text-white"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Confirm dialog */}
      <AlertDialog open={!!pendingUser} onOpenChange={(o) => !o && !submitting && setPendingUser(null)}>
        <AlertDialogContent data-testid="confirm-disable-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              {pendingUser?.currentlyDisabled ? (
                <><UserCheck className="h-5 w-5 text-[#5B9B2F]" /> Reactivar cuenta</>
              ) : (
                <><UserX className="h-5 w-5 text-red-500" /> Deshabilitar cuenta</>
              )}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingUser?.currentlyDisabled
                ? <>Vas a reactivar el acceso de <strong>{pendingUser?.email}</strong>. La cuenta podrá iniciar sesión y usar la plataforma normalmente.</>
                : <>Vas a deshabilitar a <strong>{pendingUser?.email}</strong>. La cuenta no podrá iniciar sesión y todas las sesiones activas devolverán 403 en la próxima request. Los datos no se borran y la cuenta se reactiva automáticamente si paga la plataforma.</>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={submitting} data-testid="confirm-cancel-btn">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmToggle(); }}
              disabled={submitting}
              data-testid="confirm-toggle-btn"
              className={pendingUser?.currentlyDisabled ? 'bg-[#5B9B2F] hover:bg-[#4a8126]' : 'bg-red-600 hover:bg-red-700'}
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : pendingUser?.currentlyDisabled ? 'Sí, reactivar' : 'Sí, deshabilitar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modal: crear cuenta de prueba gratis */}
      <AlertDialog open={showGrantModal} onOpenChange={(o) => !o && !granting && setShowGrantModal(false)}>
        <AlertDialogContent data-testid="grant-free-trial-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Gift className="h-5 w-5 text-[#5B9B2F]" /> Crear cuenta de prueba gratis
            </AlertDialogTitle>
            <AlertDialogDescription>
              Se crea una cuenta con acceso <strong>full</strong> y se envía un email para que la persona elija su propia contraseña.
              Quedará marcada como &quot;Regalo&quot; en el panel.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Email *</label>
              <div className="relative">
                <Mail className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                <Input
                  type="email"
                  value={grantEmail}
                  onChange={(e) => setGrantEmail(e.target.value)}
                  placeholder="alumno@dominio.com"
                  data-testid="grant-email-input"
                  className="pl-8"
                  autoFocus
                />
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Nombre (opcional)</label>
              <Input
                type="text"
                value={grantName}
                onChange={(e) => setGrantName(e.target.value)}
                placeholder="Nombre de la persona"
                data-testid="grant-name-input"
              />
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={granting} data-testid="grant-cancel-btn">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleGrantSubmit(); }}
              disabled={granting || !grantEmail.trim()}
              data-testid="grant-submit-btn"
              className="bg-[#5B9B2F] hover:bg-[#4a8126]"
            >
              {granting ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Crear y enviar acceso</>}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modal: reenviar el acceso a un mail que pagó (aunque no tenga cuenta). */}
      <AlertDialog open={showReenviar} onOpenChange={(o) => !o && !reenviando && setShowReenviar(false)}>
        <AlertDialogContent data-testid="reenviar-acceso-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5 text-[#5B9B2F]" /> Reenviar acceso
            </AlertDialogTitle>
            <AlertDialogDescription>
              Para quien pagó o tiene acceso dado por vos. Si ya tiene cuenta, le llega un link para
              crear su contraseña. Si pagó y todavía no se registró, el link para crear la cuenta.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="relative py-2">
            <Mail className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <Input
              type="email"
              value={reenviarEmail}
              onChange={(e) => setReenviarEmail(e.target.value)}
              placeholder="mail con el que pagó"
              data-testid="reenviar-acceso-email"
              className="pl-8"
              autoFocus
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={!!reenviando}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); reenviarAcceso(reenviarEmail); }}
              disabled={!!reenviando || !reenviarEmail.trim()}
              data-testid="reenviar-acceso-submit"
              className="bg-[#5B9B2F] hover:bg-[#4a8126]"
            >
              {reenviando ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Mandar acceso</>}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Link para crear contraseña nueva (o la cuenta): se manda por WhatsApp
          cuando el mail no llega, cae en spam o no se pudo mandar. */}
      <AlertDialog open={!!linkReset} onOpenChange={(abierto) => { if (!abierto) setLinkReset(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {linkDeCuentaNueva ? 'Link para crear la cuenta' : 'Link para crear contraseña nueva'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {linkDeCuentaNueva ? (
                <>Para <strong>{linkReset?.email}</strong>. Pagó y todavía no creó la cuenta: con este link la crea con ese mismo mail.</>
              ) : (
                <>Para <strong>{linkReset?.email}</strong>. Vale por 24 horas y se usa una sola vez.
                Si generás otro, este deja de funcionar.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {linkReset?.error && (
            <div className="rounded-md border border-red-300 bg-red-50 p-2.5 text-xs text-red-800" data-testid="reenviar-acceso-error">
              <strong>El mail no salió:</strong> {linkReset.error}. Mandale este link por WhatsApp.
            </div>
          )}
          <div
            className="rounded-md border bg-slate-50 p-2.5 text-xs font-mono break-all text-slate-700"
            data-testid="link-contrasena-valor"
          >
            {linkReset?.link}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={copiarLink} className="gap-1.5">
              <Copy className="h-3.5 w-3.5" /> Copiar link
            </Button>
            <a
              href={`https://wa.me/?text=${encodeURIComponent(mensajeWhatsApp)}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button size="sm" className="gap-1.5 bg-[#25D366] hover:bg-[#1ebe5b] text-white">
                <MessageCircle className="h-3.5 w-3.5" /> Mandar por WhatsApp
              </Button>
            </a>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cerrar</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
